"use client"
import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { CheckCircle } from "lucide-react"
import { useModal } from "@/context/modal-context"
import { serviceData } from "./service-data"
import { SimpleSlidingBrandLogos } from "@/components/simple-sliding-brand-logos"
import "./services.css"

export default function ServicesPageClient() {
  const { openRequestForm } = useModal()

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section with centered text and increased width */}
      <section className="bg-gradient-to-br from-gray-900 to-red-900 text-white py-8 sm:py-12 text-center">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl mx-auto text-center text-balance">
            What We Repair – Dryers, Washers, Refrigerators & More
          </h1>
          <div className="w-20 h-1 bg-white mt-3 mb-4 mx-auto rounded-full"></div>
          <p className="mt-3 text-base text-gray-300 w-full max-w-4xl mx-auto text-center text-pretty">
            We provide professional repair services for all major household appliances. Our expert technicians are
            trained to diagnose and fix problems quickly and efficiently.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-8 sm:py-12 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-16 md:gap-20">
            {serviceData.map((service, index) => (
              <div key={service.title} className="grid grid-cols-1 gap-8 lg:grid-cols-2 items-center">
                <div className={`order-2 ${index % 2 === 0 ? "lg:order-1" : "lg:order-2"}`}>
                  <h2 className="text-2xl font-bold tracking-tight text-gray-900">{service.title}</h2>
                  <p className="mt-3 text-gray-600">{service.description}</p>
                  <ul className="mt-4 space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start feature-item">
                        <CheckCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="ml-2 text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6">
                    <Button
                      asChild
                      className="bg-red-600 hover:bg-zinc-700 text-white hover:text-white border border-red-600 hover:border-zinc-700"
                    >
                      <Link href="/request-service">
                        <span className="font-bold">Request Service Now</span>
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className={`order-1 ${index % 2 === 0 ? "lg:order-2" : "lg:order-1"} flex justify-center`}>
                  <Image
                    src={service.image || "/placeholder.svg"}
                    alt={service.alt}
                    width={320}
                    height={320}
                    className="h-56 w-auto object-contain service-icon"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section - Replaced with Simple Sliding Brand Logos */}
      <SimpleSlidingBrandLogos />

      {/* CTA Section with centered text and increased width */}
      <section className="bg-gradient-to-br from-gray-900 to-red-900 text-white py-8 sm:py-12 text-center">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mx-auto text-center">
            Need appliance repair in Massachusetts?
          </h2>
          <p className="mt-3 text-base w-full max-w-4xl mx-auto text-center">
            Contact us today for fast, reliable service from our expert technicians.
          </p>
          <div className="mt-6">
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-red-600 text-red-800 hover:text-white border border-white"
            >
              <Link href="/request-service">
                <span className="font-bold">Request Service Now</span>
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}
